import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Download, Loader2, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/faida`;
const dt = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const DELIVERY_CLS = { opened: 'text-emerald-700', delivered: 'text-emerald-600', sent: 'text-sky-700', bounced: 'text-red-600', failed: 'text-red-600' };
const WAITING = ['pending_whi_registration', 'payment_reported'];

const Trail = ({ trail }) => (
  <table className="mt-2 w-full text-[11px]">
    <thead className="text-left text-slate-500"><tr><th>Type</th><th>To</th><th>Sent</th><th>Message ID</th><th>Delivered</th><th>Opened</th><th>Bounced</th></tr></thead>
    <tbody>
      {trail.map((t) => (
        <tr key={`${t.email_id}-${t.to}-${t.n}-${t.kind}`} className="border-t border-slate-100">
          <td>{t.kind}{t.n ? ` #${t.n}` : ''}{t.copy ? ' (copy)' : ''}</td><td>{t.to}</td><td>{dt(t.sent_at)}{t.status === 'failed' && <b className="text-red-600"> failed</b>}</td>
          <td className="select-all font-mono">{t.email_id || '—'}</td><td>{dt(t.delivered_at)}</td><td>{dt(t.opened_at)}</td><td className="text-red-600">{t.bounced_at ? dt(t.bounced_at) : '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Row = ({ r, headers, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const remind = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/registrations/${r.lead_id}/remind`, {}, { headers }); toast.success(`Reminder #${data.n} sent to WHI`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const [pdfBusy, setPdfBusy] = useState(false);
  const proof = async () => {
    setPdfBusy(true);
    const res = await axios.get(`${API}/registrations/${r.lead_id}/proof.pdf`, { headers, responseType: 'blob' }).catch(() => null);
    setPdfBusy(false);
    if (!res) return toast.error('Could not build proof');
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `WHI-proof-${r.lead_id}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const waiting = WAITING.includes(r.status);
  return (
    <div className="border-t border-slate-100 py-3" data-testid={`whi-tracker-row-${r.lead_id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <button onClick={() => setOpen(!open)} className="font-mono font-semibold text-slate-900 underline-offset-2 hover:underline" data-testid={`whi-tracker-toggle-${r.lead_id}`}>{r.lead_id}</button>
        <span className="text-slate-700">{r.full_name}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5">{r.status.replace(/_/g, ' ')}</span>
        {waiting && <span className={`font-semibold ${r.hours_waiting >= 24 ? 'text-red-600' : 'text-amber-600'}`} data-testid={`whi-tracker-age-${r.lead_id}`}>{Math.round(r.hours_waiting)}h waiting</span>}
        <span className={`font-semibold ${DELIVERY_CLS[r.summary.delivery] || ''}`} data-testid={`whi-tracker-delivery-${r.lead_id}`}>{r.summary.delivery || 'not sent'}</span>
        <span className="text-slate-500">reminders {r.reminders_sent + r.payment_reminders_sent}</span>
        {r.escalated_at && <span className="font-semibold text-red-600">escalated</span>}
        <span className="text-slate-500">WHI viewed: {dt(r.whi_last_seen_at)}</span>
        <div className="ml-auto flex gap-2">
          {waiting && <button disabled={busy} onClick={remind} className="inline-flex items-center gap-1 rounded-lg border border-amber-400 px-2 py-1 font-semibold text-amber-700 disabled:opacity-50" data-testid={`whi-tracker-remind-${r.lead_id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />}Send reminder</button>}
          <button disabled={pdfBusy} onClick={proof} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 font-semibold disabled:opacity-50" data-testid={`whi-tracker-proof-${r.lead_id}`}>{pdfBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}Proof PDF</button>
        </div>
      </div>
      {open && <Trail trail={r.trail} />}
    </div>
  );
};

export const AdminWhiTracker = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const headers = { 'X-Admin-Key': adminKey };
  const load = useCallback(() => axios.get(`${API}/tracker`, { headers: { 'X-Admin-Key': adminKey } }).then((r) => setData(r.data)).catch(() => setData({ rows: [] })), [adminKey]);
  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);
  if (!data) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-whi-tracker">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <MailCheck className="h-4 w-4 text-emerald-700" /><h3 className="text-sm font-bold text-slate-900">WHI Requests tracker</h3>
        <span className="text-xs text-slate-500">Desk last opened by WHI: {dt(data.desk_last_viewed_at)}</span>
        {!data.webhook_configured && <span className="text-xs font-semibold text-amber-700" data-testid="whi-tracker-webhook-warning">Delivery/open tracking off — add the Resend webhook secret</span>}
      </div>
      <p className="text-xs text-slate-500">Auto-reminders Mon–Fri 09:00 EAT after 24h · escalation to ops after 3 reminders.</p>
      {data.rows.length ? data.rows.map((r) => <Row key={r.lead_id} r={r} headers={headers} onDone={load} />)
        : <p className="mt-2 text-sm text-slate-500">No Faida requests yet.</p>}
    </div>
  );
};
